import { ANY_EMPLOYEE_ID } from '@/features/booking-selection/constants';
import { serviceIsExtraTimeAddOn } from '@/helpers/service';
import { BookState } from '@/types/state/book';
import { Place } from '@/types/state/shared';
import { BookingFlowSelectionPatch } from './urlState';

function joinIds(ids: Array<number | undefined>): string | null {
  const present = ids.filter((id): id is number => id != null).map(String);
  return present.length > 0 ? present.join(',') : null;
}

/**
 * The inverse of hydration for the params a later screen can change: `ss` from the main
 * services, `rs` from the recommended add-ons and `sr` from the employee. Extra-time add-ons
 * are `et`'s to signal, so they never enter `rs`; `et` itself is left out because no screen
 * downstream of `/extra-tid` changes the extra-time answer. `sr` carries the employee's
 * externalId (the any-employee sentinel as-is), matching what `resolveEmployeeFromUrl` reads.
 *
 * `sr` is only ever set, never deleted: `REMOVE_EMPLOYEE` deletes `state.book.employee` rather
 * than zeroing it, so an absent employee is both "picked Valfri utövare on the calendar" and
 * "never answered" and the store cannot tell them apart. Deleting an `sr` the entry already
 * carries would read as the second and send the visitor back to `/valj-utovare`, so an absent
 * employee leaves the param alone.
 *
 * `ss` is set-only for a different reason: the flow gives no way to end up with no main service
 * (`/tillval` refuses to remove the last one, and its remain rule counts mains), so an empty one
 * here cannot be an edit to carry back. It means the store is *older* than the entry — a forward
 * pop onto a step the visitor already left, where deleting `ss` would strand them on a step whose
 * guard bounces them back. `rs` does delete: removing the last add-on is exactly the edit a pop
 * out of the calendar has to preserve.
 */
export function selectionPatchFromBookState(bookstate: BookState, place: Place): BookingFlowSelectionPatch {
  const services = (bookstate.services ?? []).filter((service) => !serviceIsExtraTimeAddOn(service));
  const mainServices = services.filter((service) => !service.about?.settings?.isAddOn);
  const addOns = services.filter((service) => service.about?.settings?.isAddOn);

  const employeeId = bookstate.employee;
  let sr: string | undefined;
  if (employeeId === ANY_EMPLOYEE_ID) {
    sr = String(ANY_EMPLOYEE_ID);
  } else if (employeeId != null) {
    sr = (place.employees ?? []).find((employee) => employee.id === employeeId)?.about?.externalId ?? undefined;
  }

  return {
    ss: joinIds(mainServices.map((service) => service.id)) ?? undefined,
    rs: joinIds(addOns.map((service) => service.id)),
    sr,
  };
}
